import { ChangeEvent, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  AdminZTokenPackageDto,
  createAdminZTokenPackage,
  deleteAdminZTokenPackage,
  updateAdminZTokenPackage,
} from "../api/adminEconomyApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import useAdminZTokenPackages from "../hooks/useAdminZTokenPackages";
import { resolveAssetUrl, uploadAdminAsset } from "../utils/assets";

const resolveImageUrl = (raw?: string | null) => resolveAssetUrl(raw);
const IMAGE_UPLOAD_HINT = "Format: PNG/JPG/JPEG/WEBP/SVG/GIF/AVIF • Maksimum: 10 MB";

const createEmptyPackage = (sortOrder: number, currency = "TRY"): AdminZTokenPackageDto => ({
  id: "",
  name: "",
  tokenAmount: 0,
  price: 0,
  currency,
  description: "",
  sortOrder,
  isActive: true,
  imageUrl: null,
});

export default function AdminZTokenPackages() {
  const { loading: checkingPermission, allowed } = usePermissionCheck("ZTokens.View");
  const { allowed: canEdit } = usePermissionCheck("ZTokens.Manage");
  const { data: packages = [], isLoading, mutate } = useAdminZTokenPackages(true);
  const [newPkg, setNewPkg] = useState<AdminZTokenPackageDto>(() => createEmptyPackage(1));
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const getUploadErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }
    return "Gorsel yuklenemedi. Lutfen tekrar deneyin.";
  };

  const filteredPackages = useMemo(() => {
    if (!filter.trim()) {
      return packages;
    }
    const term = filter.trim().toLowerCase();
    return packages.filter((pkg) => pkg.name.toLowerCase().includes(term));
  }, [packages, filter]);

  const optimisticUpdate = (updater: (list: AdminZTokenPackageDto[]) => AdminZTokenPackageDto[]) => {
    mutate((current) => updater([...(current ?? [])]), false);
  };

  useEffect(() => {
    setNewPkg((prev) =>
      prev.name || prev.tokenAmount > 0 ? prev : createEmptyPackage(Math.max(packages.length + 1, 1), prev.currency),
    );
  }, [packages]);

  if (checkingPermission) {
    return <div className="text-sm text-gray-500">Yetki kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-700">Bu alanı görüntüleme yetkin yok.</div>;
  }

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>, target: "new" | string) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!canEdit) {
      Swal.fire("Yetki yok", "Görsel yükleme yetkin bulunmuyor.", "warning");
      return;
    }

    setUploading(target);
    try {
      const fileName = await uploadAdminAsset("/admin/packages/upload-image", file);
      if (!fileName) {
        throw new Error("upload failed");
      }
      if (target === "new") {
        setNewPkg((pkg) => ({ ...pkg, imageUrl: fileName }));
      } else {
        optimisticUpdate((list) => list.map((pkg) => (pkg.id === target ? { ...pkg, imageUrl: fileName } : pkg)));
      }
    } catch (error) {
      console.error("[admin] upload token package image failed", error);
      Swal.fire("Hata", getUploadErrorMessage(error), "error");
    } finally {
      setUploading(null);
    }
  };

  const refreshList = async () => {
    await mutate();
  };

  const handleCreate = async () => {
    if (!canEdit) {
      Swal.fire("Yetki yok", "Paket oluşturma yetkin bulunmuyor.", "warning");
      return;
    }
    if (!newPkg.name.trim()) {
      Swal.fire("Eksik bilgi", "Paket adı zorunlu.", "warning");
      return;
    }
    const payload = {
      name: newPkg.name.trim(),
      tokenAmount: newPkg.tokenAmount,
      price: newPkg.price,
      currency: newPkg.currency,
      description: newPkg.description ?? "",
      sortOrder: newPkg.sortOrder,
      isActive: newPkg.isActive,
      imageUrl: newPkg.imageUrl ?? "",
    };
    const res = await createAdminZTokenPackage(payload);
    if (res.success) {
      Swal.fire("Oluşturuldu", "Z-Token paketi eklendi.", "success");
      setNewPkg(createEmptyPackage(newPkg.sortOrder + 1, newPkg.currency));
      refreshList();
    } else {
      Swal.fire("Hata", "Paket oluşturulamadı.", "error");
    }
  };

  const handleSave = async (pkg: AdminZTokenPackageDto) => {
    if (!canEdit) {
      Swal.fire("Yetki yok", "Paket düzenleme yetkin bulunmuyor.", "warning");
      return;
    }
    const res = await updateAdminZTokenPackage(pkg.id, {
      name: pkg.name,
      tokenAmount: pkg.tokenAmount,
      price: pkg.price,
      currency: pkg.currency,
      description: pkg.description ?? "",
      sortOrder: pkg.sortOrder,
      isActive: pkg.isActive,
      imageUrl: pkg.imageUrl ?? "",
    });
    if (res.success) {
      Swal.fire("Kaydedildi", "Paket güncellendi.", "success");
      refreshList();
    } else {
      Swal.fire("Hata", "Paket güncellenemedi.", "error");
    }
  };

  const handleDelete = async (pkg: AdminZTokenPackageDto) => {
    if (!canEdit) {
      Swal.fire("Yetki yok", "Paket silme yetkin bulunmuyor.", "warning");
      return;
    }
    const confirm = await Swal.fire({
      title: "Silmek istediğine emin misin?",
      text: `${pkg.name} kalıcı olarak silinecek.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, sil",
      cancelButtonText: "İptal",
    });
    if (!confirm.isConfirmed) {
      return;
    }
    const res = await deleteAdminZTokenPackage(pkg.id);
    if (res.success) {
      Swal.fire("Silindi", "Paket kaldırıldı.", "success");
      refreshList();
    } else {
      Swal.fire("Hata", "Paket silinemedi.", "error");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Z-Token Paketleri</h1>
        <p className="text-sm text-gray-500">Kullanıcıların satın alabileceği Z-Token paketlerini yönetin.</p>
      </div>

      <section className="rounded bg-white p-4 shadow">
        <div className="mb-3 text-sm font-semibold text-gray-700">Yeni paket oluştur</div>
        <div className="grid gap-3 md:grid-cols-6 text-sm">
          <input
            placeholder="Paket adı"
            value={newPkg.name}
            onChange={(e) => setNewPkg((prev) => ({ ...prev, name: e.target.value }))}
            className="rounded border border-gray-300 p-2"
          />
          <input
            type="number"
            placeholder="Z-Token miktarı"
            value={newPkg.tokenAmount}
            onChange={(e) => setNewPkg((prev) => ({ ...prev, tokenAmount: Number(e.target.value) }))}
            className="rounded border border-gray-300 p-2"
          />
          <input
            type="number"
            placeholder="Fiyat"
            value={newPkg.price}
            onChange={(e) => setNewPkg((prev) => ({ ...prev, price: Number(e.target.value) }))}
            className="rounded border border-gray-300 p-2"
          />
          <select
            value={newPkg.currency}
            onChange={(e) => setNewPkg((prev) => ({ ...prev, currency: e.target.value }))}
            className="rounded border border-gray-300 p-2"
          >
            <option value="TRY">TRY</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
          <input
            type="number"
            placeholder="Sıra"
            value={newPkg.sortOrder}
            onChange={(e) => setNewPkg((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
            className="rounded border border-gray-300 p-2"
          />
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={newPkg.isActive}
              onChange={(e) => setNewPkg((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Aktif
          </label>
        </div>
        <textarea
          placeholder="Açıklama (opsiyonel)"
          value={newPkg.description ?? ""}
          onChange={(e) => setNewPkg((prev) => ({ ...prev, description: e.target.value }))}
          className="mt-3 w-full rounded border border-gray-300 p-2 text-sm"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
          <label className="cursor-pointer rounded border border-dashed border-gray-300 px-3 py-2">
            {uploading === "new" ? "Yükleniyor..." : newPkg.imageUrl ? "Görseli değiştir" : "Görsel yükle"}
            <input type="file" accept="image/*" className="sr-only" onChange={(e) => handleUpload(e, "new")} />
          </label>
          <span className="text-[11px] text-gray-500">{IMAGE_UPLOAD_HINT}</span>
          {newPkg.imageUrl && (
            <div className="flex items-center gap-2">
              <img
                src={resolveImageUrl(newPkg.imageUrl)}
                alt={newPkg.name || "Yeni paket görseli"}
                className="h-16 w-16 rounded border border-gray-200 object-cover"
              />
              <button
                type="button"
                onClick={() => setNewPkg((prev) => ({ ...prev, imageUrl: null }))}
                className="rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700"
              >
                Kaldır
              </button>
            </div>
          )}
          <button
            onClick={handleCreate}
            className="ml-auto rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            disabled={!canEdit}
          >
            Z-Token paketi oluştur
          </button>
        </div>
      </section>

      <section className="rounded bg-white p-4 shadow">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="text-sm font-semibold text-gray-700">Mevcut paketler</div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Ada göre filtrele"
            className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <span className="text-xs text-gray-500">
            {filteredPackages.length}/{packages.length}
          </span>
        </div>
        {isLoading ? (
          <div className="text-sm text-gray-500">Yükleniyor...</div>
        ) : filteredPackages.length === 0 ? (
          <div className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-500">Paket bulunmuyor.</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredPackages.map((pkg) => {
              const preview = resolveImageUrl(pkg.imageUrl);
              return (
                <div key={pkg.id} className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <input
                      value={pkg.name}
                      onChange={(e) => optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, name: e.target.value } : item)))}
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm font-semibold"
                    />
                  </div>
                  {preview && (
                    <img src={preview} alt={pkg.name} className="h-32 w-full rounded border border-gray-200 object-cover" />
                  )}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <label className="text-gray-600">
                      Z-Token
                      <input
                        type="number"
                        value={pkg.tokenAmount}
                      onChange={(e) =>
                        optimisticUpdate((list) =>
                            list.map((item) => (item.id === pkg.id ? { ...item, tokenAmount: Number(e.target.value) } : item)),
                          )
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                      />
                    </label>
                    <label className="text-gray-600">
                      Fiyat
                      <input
                        type="number"
                        value={pkg.price}
                        onChange={(e) =>
                          optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, price: Number(e.target.value) } : item)))
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                      />
                    </label>
                    <label className="text-gray-600">
                      Para birimi
                      <select
                        value={pkg.currency}
                        onChange={(e) =>
                          optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, currency: e.target.value } : item)))
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                      >
                        <option value="TRY">TRY</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </label>
                    <label className="text-gray-600">
                      Sıra
                      <input
                        type="number"
                        value={pkg.sortOrder}
                        onChange={(e) =>
                          optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, sortOrder: Number(e.target.value) } : item)))
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                      />
                    </label>
                  </div>
                  <textarea
                    value={pkg.description ?? ""}
                    onChange={(e) =>
                      optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, description: e.target.value } : item)))
                    }
                    className="rounded border border-gray-300 p-2 text-sm"
                    placeholder="Açıklama"
                  />
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <label className="cursor-pointer rounded bg-slate-800 px-3 py-1 font-semibold text-white hover:bg-slate-700">
                      {uploading === pkg.id ? "Yükleniyor..." : "Görsel yükle"}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(e) => handleUpload(e, pkg.id)}
                      />
                    </label>
                    <span className="text-[11px] text-gray-500">{IMAGE_UPLOAD_HINT}</span>
                    {pkg.imageUrl && (
                      <button
                        type="button"
                        onClick={() => optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, imageUrl: null } : item)))}
                        className="rounded bg-red-600 px-3 py-1 text-white"
                      >
                        Görseli kaldır
                      </button>
                    )}
                    <label className="ml-auto flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={pkg.isActive}
                        onChange={(e) =>
                          optimisticUpdate((list) => list.map((item) => (item.id === pkg.id ? { ...item, isActive: e.target.checked } : item)))
                        }
                      />
                      Aktif
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave(pkg)}
                      className="flex-1 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                      disabled={!canEdit}
                    >
                      Kaydet
                    </button>
                    <button
                      onClick={() => handleDelete(pkg)}
                      className="rounded bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700"
                      disabled={!canEdit}
                    >
                      Sil
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

